import { DateTime } from "luxon";

import { FIELD_NAME_DATE, FIELD_NAME_TIME_RANGE, type TextFieldValue } from "../../atoms/Form";
import {
  type DayOfWeek,
  type FormValues,
  type MeetingTimeData,
  type MeetingTimeSlot,
} from "./AddEventTimesModal.types";

export const FIELD_NAME_CAPACITY = "capacity";
export const FIELD_NAME_SERIES_ID = "seriesId";

export const BUILDER_FIELD_DATE = "builder_date";
export const BUILDER_FIELD_CAPACITY = "builder_capacity";
export const BUILDER_FIELD_SERIES_ID = "builder_seriesId";
export const BUILDER_TIME_SLOT_ID = "0";

export const generateTimeRangeFieldName = (slotId: string): string => `timeRange_${slotId}`;
export const generateBuilderTimeRangeFieldName = (slotId: string): string => `builder_timeRange_${slotId}`;

// Centralized strings for internationalization
export const STRINGS = {
  tAddAnotherTime: "Add another time",
  tAddEventTimes: "Create",
  tCancel: "Cancel",
  tCapacity: "Capacity",
  tCreate: "Create Event Times",
  tDate: "Date",
  tGenerate: "Generate Event Times",
  tMeetingTime: "Event Time",
  tRecurrence: "Recurrence",
  tRemoveTime: "Remove time",
  tReset: "Reset",
  tReviewHeading: "Review event times",
  tSeries: "Series",
  tTimeRange: "Time",
  tTryVoiceNow: "Try voice now!",
  tUseManualBuilder: "Use manual builder",
  tVoiceApplyDescription: "Apply description",
  tVoiceDescriptionHint: "Describe your schedule in plain language, edit the text if needed, then apply it.",
  tVoiceDescriptionLabel: "Schedule description",
  tVoiceDescriptionPlaceholder: "Every Tuesday at 9am for the next 8 weeks with capacity 25",
  tVoiceStartRecording: "Record",
  tVoiceStopRecording: "Stop recording",
  tVoiceTranscribing: "Transcribing...",
  tWizardBackToGenerate: "Back",
  tWizardContinueToReview: "Review event times",
  tWizardStepGenerate: "Generate",
  tWizardStepReview: "Review",
} as const;

export const DAYS_OF_WEEK: readonly DayOfWeek[] = [
  { fullName: "Sunday", key: 0, label: "S" },
  { fullName: "Monday", key: 1, label: "M" },
  { fullName: "Tuesday", key: 2, label: "T" },
  { fullName: "Wednesday", key: 3, label: "W" },
  { fullName: "Thursday", key: 4, label: "T" },
  { fullName: "Friday", key: 5, label: "F" },
  { fullName: "Saturday", key: 6, label: "S" },
] as const;

export const generateFieldName = (id: string, field: string): string => `meetingTime_${id}_${field}`;

export const getUserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getNextSelectedDay = (currentDate: DateTime, selectedDays: Set<number>): DateTime => {
  if (selectedDays.size === 0) {
    // If no days selected, just add 1 day
    return currentDate.plus({ days: 1 });
  }

  const selectedDaysArray = Array.from(selectedDays).sort();
  // Convert Luxon weekday (1=Monday, 7=Sunday) to dayjs format (0=Sunday, 6=Saturday)
  const currentDayOfWeek = currentDate.weekday === 7 ? 0 : currentDate.weekday;

  // Find the next selected day in the same week
  const nextDayInWeek = selectedDaysArray.find((day) => day > currentDayOfWeek);

  if (nextDayInWeek !== undefined) {
    // Found a day later this week
    const daysToAdd = nextDayInWeek - currentDayOfWeek;
    return currentDate.plus({ days: daysToAdd });
  } else {
    // No more days this week, wrap to next week's first selected day
    const firstSelectedDay = selectedDaysArray[0];
    const daysToAdd = 7 - currentDayOfWeek + firstSelectedDay;
    return currentDate.plus({ days: daysToAdd });
  }
};

export const isMeetingTimeComplete = (slotId: string, formValues: FormValues): boolean => {
  const date = formValues[generateFieldName(slotId, FIELD_NAME_DATE)] as DateTime | null;
  const timeRange = formValues[generateFieldName(slotId, FIELD_NAME_TIME_RANGE)] as [DateTime | null, DateTime | null];
  const capacity = formValues[generateFieldName(slotId, "capacity")];

  // First check if all fields are filled
  if (!date || !timeRange || !timeRange[0] || !timeRange[1] || capacity === undefined) {
    return false;
  }

  const now = DateTime.now();
  const userTimezone = getUserTimezone();
  const localDate = date.setZone(userTimezone);
  const localStartTime = timeRange[0].setZone(userTimezone);
  const localEndTime = timeRange[1].setZone(userTimezone);

  // Combine date with times for proper comparison
  const startDateTime = localDate.set({
    hour: localStartTime.hour,
    millisecond: 0,
    minute: localStartTime.minute,
    second: 0,
  });

  const endDateTime = localDate.set({
    hour: localEndTime.hour,
    millisecond: 0,
    minute: localEndTime.minute,
    second: 0,
  });

  // Check if date is in the past
  if (localDate < now.startOf("day")) {
    return false;
  }

  // If it's today, check if times are in the past
  if (localDate.hasSame(now, "day")) {
    if (startDateTime < now || endDateTime < now) {
      return false;
    }
  }

  return true;
};

export const generateRequiredFields = (meetingTimeSlots: MeetingTimeSlot[]): string[] => {
  const fields: string[] = [];
  for (const slot of meetingTimeSlots) {
    fields.push(
      generateFieldName(slot.id, FIELD_NAME_DATE),
      generateFieldName(slot.id, FIELD_NAME_TIME_RANGE),
      generateFieldName(slot.id, "capacity"),
    );
  }
  return fields;
};

export const buildMeetingTimeFromParts = ({
  capacity,
  date,
  endTime,
  seriesId,
  startTime,
}: {
  capacity: number;
  date: DateTime;
  endTime: DateTime;
  seriesId?: string;
  startTime: DateTime;
}): MeetingTimeData => {
  const tz = getUserTimezone();
  const localDate = date.setZone(tz).startOf("day");
  const localStart = startTime.setZone(tz);
  const localEnd = endTime.setZone(tz);

  return {
    capacity,
    date: localDate,
    endTime: localDate.set({
      hour: localEnd.hour,
      millisecond: 0,
      minute: localEnd.minute,
      second: 0,
    }),
    seriesId,
    startTime: localDate.set({
      hour: localStart.hour,
      millisecond: 0,
      minute: localStart.minute,
      second: 0,
    }),
  };
};

export const extractMeetingTimesFromForm = (
  formValues: FormValues,
  meetingTimeSlots: MeetingTimeSlot[],
): MeetingTimeData[] => {
  const meetingTimes: MeetingTimeData[] = [];
  const userTimezone = getUserTimezone();

  for (const slot of meetingTimeSlots) {
    const dateFieldName = generateFieldName(slot.id, FIELD_NAME_DATE);
    const timeRangeFieldName = generateFieldName(slot.id, FIELD_NAME_TIME_RANGE);
    const capacityFieldName = generateFieldName(slot.id, "capacity");
    const seriesIdFieldName = generateFieldName(slot.id, "seriesId");

    const date = formValues[dateFieldName] as DateTime | null;
    const timeRange = formValues[timeRangeFieldName] as [DateTime | null, DateTime | null];
    const capacity = formValues[capacityFieldName];
    const seriesId = formValues[seriesIdFieldName] as string | null;

    if (date && timeRange && timeRange[0] && timeRange[1] && capacity !== undefined && capacity !== null) {
      // Use the user's local timezone for proper DST handling
      const localDate = date.setZone(userTimezone);
      const localStartTime = timeRange[0].setZone(userTimezone);
      const localEndTime = timeRange[1].setZone(userTimezone);

      // Combine date with start/end times, preserving local time regardless of DST
      const startTime = localStartTime.set({
        day: localDate.day,
        month: localDate.month,
        year: localDate.year,
      });

      const endTime = localEndTime.set({
        day: localDate.day,
        month: localDate.month,
        year: localDate.year,
      });

      meetingTimes.push({
        capacity: Number(capacity),
        date: localDate,
        endTime,
        seriesId: seriesId || undefined,
        startTime,
      });
    }
  }

  return meetingTimes;
};

export const formatDateTimeForSummary = (date: DateTime, timeRange: [DateTime | null, DateTime | null]): string => {
  if (!date?.toFormat || !timeRange[0]?.toFormat || !timeRange[1]?.toFormat) {
    return "";
  }

  // Format the date as "Wednesday, July 16th 2025"
  const formattedDate = date.toFormat("cccc, MMMM d, yyyy");

  // Use the user's local timezone instead of hardcoding Eastern
  const userTimezone = getUserTimezone();

  const localStartTime = timeRange[0] ? timeRange[0].setZone(userTimezone) : null;
  const localEndTime = timeRange[1] ? timeRange[1].setZone(userTimezone) : null;

  // Format the times with abbreviated timezone (EST/EDT, PST/PDT, etc.)
  const startTime = localStartTime ? localStartTime.toFormat("h:mma") : "";
  const endTime = localEndTime ? localEndTime.toFormat("h:mma") : "";

  return `${formattedDate} at ${startTime} - ${endTime}`;
};

export const formatCapacityText = (capacity: unknown): string => {
  return Number(capacity) === 0 ? "Unlimited" : capacity?.toString() || "0";
};

export const isEventTimeInPast = (
  date: DateTime | null,
  timeRange: [DateTime | null, DateTime | null] | null,
): boolean => {
  if (!date || !timeRange || !timeRange[0]) {
    return false;
  }

  const now = DateTime.now();
  const userTimezone = getUserTimezone();
  const localDate = date.setZone(userTimezone);
  const localStartTime = timeRange[0].setZone(userTimezone);

  // Combine date with start time for proper comparison
  const startDateTime = localDate.set({
    hour: localStartTime.hour,
    millisecond: 0,
    minute: localStartTime.minute,
    second: 0,
  });

  // Check if date is in the past
  if (localDate < now.startOf("day")) {
    return true;
  }

  // If it's today, check if start time is in the past
  if (localDate.hasSame(now, "day")) {
    if (startDateTime < now) {
      return true;
    }
  }

  return false;
};

export const extractTimeRangesFromForm = (formValues: FormValues, timeSlotIds: string[]): [DateTime, DateTime][] => {
  const timeRanges: [DateTime, DateTime][] = [];

  for (const slotId of timeSlotIds) {
    const timeRange = formValues[generateTimeRangeFieldName(slotId)] as [DateTime | null, DateTime | null] | undefined;

    if (timeRange?.[0] && timeRange[1]) {
      timeRanges.push([timeRange[0], timeRange[1]]);
    }
  }

  return timeRanges;
};

export const isEventTimeFormComplete = (formValues: FormValues, timeSlotIds: string[]): boolean => {
  const date = formValues[FIELD_NAME_DATE] as DateTime | null;
  const capacity = formValues[FIELD_NAME_CAPACITY];

  if (!date || capacity === undefined || capacity === null) {
    return false;
  }

  if (timeSlotIds.length === 0) {
    return false;
  }

  for (const slotId of timeSlotIds) {
    const timeRange = formValues[generateTimeRangeFieldName(slotId)] as [DateTime | null, DateTime | null] | undefined;

    if (!timeRange?.[0] || !timeRange[1]) {
      return false;
    }

    if (isEventTimeInPast(date, timeRange)) {
      return false;
    }
  }

  return true;
};

export const isBuilderFormComplete = (formValues: FormValues, builderTimeSlotIds: string[]): boolean => {
  const date = formValues[BUILDER_FIELD_DATE] as DateTime | null;
  const capacity = formValues[BUILDER_FIELD_CAPACITY];

  if (!date || capacity === undefined || capacity === null) {
    return false;
  }

  if (builderTimeSlotIds.length === 0) {
    return false;
  }

  for (const slotId of builderTimeSlotIds) {
    const timeRange = formValues[generateBuilderTimeRangeFieldName(slotId)] as
      | [DateTime | null, DateTime | null]
      | undefined;

    if (!timeRange?.[0] || !timeRange[1]) {
      return false;
    }

    if (isEventTimeInPast(date, timeRange)) {
      return false;
    }
  }

  return true;
};

export const extractBuilderTimeRangesFromForm = (
  formValues: FormValues,
  builderTimeSlotIds: string[],
): [DateTime, DateTime][] => {
  const timeRanges: [DateTime, DateTime][] = [];

  for (const slotId of builderTimeSlotIds) {
    const timeRange = formValues[generateBuilderTimeRangeFieldName(slotId)] as
      | [DateTime | null, DateTime | null]
      | undefined;

    if (timeRange?.[0] && timeRange[1]) {
      timeRanges.push([timeRange[0], timeRange[1]]);
    }
  }

  return timeRanges;
};

export const populateMeetingTimeSlotsInForm = (
  meetingTimes: MeetingTimeData[],
  slots: MeetingTimeSlot[],
  formApi: {
    change: (
      field: string,
      value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | number | null,
    ) => void;
  },
): void => {
  meetingTimes.forEach((meetingTime, index) => {
    const slot = slots[index];
    if (!slot) {
      return;
    }

    formApi.change(generateFieldName(slot.id, FIELD_NAME_DATE), meetingTime.date);
    formApi.change(generateFieldName(slot.id, FIELD_NAME_TIME_RANGE), [meetingTime.startTime, meetingTime.endTime]);
    formApi.change(generateFieldName(slot.id, "capacity"), meetingTime.capacity);

    if (meetingTime.seriesId) {
      formApi.change(generateFieldName(slot.id, "seriesId"), meetingTime.seriesId);
    }
  });
};

const EMPTY_TIME_RANGE: [DateTime | null, DateTime | null] = [null, null];

export const createResetFormValues = (
  defaultDate?: DateTime | null,
  builderTimeSlotIds: string[] = [BUILDER_TIME_SLOT_ID],
): FormValues => {
  const values: FormValues = {
    [BUILDER_FIELD_CAPACITY]: 0,
    [BUILDER_FIELD_DATE]: defaultDate ?? null,
    [BUILDER_FIELD_SERIES_ID]: null,
  };

  builderTimeSlotIds.forEach((slotId) => {
    values[generateBuilderTimeRangeFieldName(slotId)] = EMPTY_TIME_RANGE;
  });

  return values;
};

export const clearMeetingTimeSlotFields = (
  slots: MeetingTimeSlot[],
  formApi: {
    change: (
      field: string,
      value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | number | null,
    ) => void;
  },
): void => {
  slots.forEach((slot) => {
    formApi.change(generateFieldName(slot.id, FIELD_NAME_DATE), null);
    formApi.change(generateFieldName(slot.id, FIELD_NAME_TIME_RANGE), EMPTY_TIME_RANGE);
    formApi.change(generateFieldName(slot.id, "capacity"), null);
    formApi.change(generateFieldName(slot.id, "seriesId"), null);
  });
};

export const clearBuilderFields = (
  builderTimeSlotIds: string[],
  formApi: {
    change: (
      field: string,
      value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | number | null,
    ) => void;
  },
): void => {
  builderTimeSlotIds.forEach((slotId) => {
    formApi.change(generateBuilderTimeRangeFieldName(slotId), EMPTY_TIME_RANGE);
  });
  formApi.change(BUILDER_FIELD_DATE, null);
  formApi.change(BUILDER_FIELD_CAPACITY, null);
  formApi.change(BUILDER_FIELD_SERIES_ID, null);
};
